import "./scrollable.css";

import { Page, PageHeader } from "@nike/epic-react-ui";
// import useSWR from "swr";
import GraphQLService from "../shared/GraphQLService";

import React, { useState, useReducer } from "react";
import { useParams, useNavigate } from "react-router-dom";

import type { SortingState, ColumnFiltersState, ColumnOrderState, RowSelectionState, VisibilityState } from "@tanstack/react-table";

import type { Exception } from "../shared/Types";
import {  IconButton} from "@nike/eds";

import { ExceptionsTable } from "../components/exceptions-table/ExceptionsTable";
import { ColumnsSidePanel } from "../components/table-columns-sidepanel/ColumnsSidePanel";
import { getTableColumns } from "../components/exceptions-table/ExceptionTableColumns";
import { ExceptionDetail } from "../components/exception-detail/ExceptionDetail";

const api = new GraphQLService();

// const useExceptionsApi = (sorting: SortingState) => {
//   const fetcher = async () => await api.getExceptions(sorting);
//   return useSWR(["Exceptions", sorting], fetcher);
// };

type ExceptionsPageState = {
    loading: boolean;
    error: string | null;
    data: any;
    configurationIsOpen: boolean;
    sorting: SortingState;
    columnOrder: ColumnOrderState;
    columnVisibility: VisibilityState;
    viewingException?: Exception | null;
    detailIsOpen: boolean;
    rowSelection: RowSelectionState;
};

type ExceptionsPageAction =
  | { type: "FETCH_START" }
  | { type: "FETCH_SUCCESS"; payload: any }
  | { type: "FETCH_ERROR"; payload: string }
  | { type: "OPEN_CONFIGURATION"}
  | { type: "CLOSE_CONFIGURATION"}
  | { type: "SET_SORTING"; payload: SortingState }
  | { type: "SET_COLUMN_ORDER"; payload: ColumnOrderState }
  | { type: "SET_COLUMN_VISIBILITY"; payload: VisibilityState }
  | { type: "SET_VIEWING_EXCEPTION"; payload: Exception | null }
  | { type: "OPEN_DETAIL"}
  | { type: "CLOSE_DETAIL"}
  | { type: "SET_ROW_SELECTION"; payload: RowSelectionState }
  | { type: "RESET_ROW_SELECTION" };

// Helper to set all columns visible based on ExceptionTableColumns
function getAllColumnsVisible(): Record<string, boolean> {
  // getTableColumns returns an array of column definitions, each with an 'id'
  return getTableColumns().reduce((acc, col) => {
    if (col.accessorKey !== undefined) {
      acc[col.accessorKey] = true;
    }
    return acc;
  }, {} as Record<string, boolean>);
}

function exceptionsPageReducer(state: ExceptionsPageState, action: ExceptionsPageAction): ExceptionsPageState {

  switch (action.type) {
    case "FETCH_START":
      return { ...state, loading: true, error: null };
    case "FETCH_SUCCESS":
      return { ...state, loading: false, error: null, data: action.payload };
    case "FETCH_ERROR":
      return { ...state, loading: false, error: action.payload };
    case "OPEN_CONFIGURATION":
      return { ...state, configurationIsOpen: true };
    case "CLOSE_CONFIGURATION":
      return { ...state, configurationIsOpen: false };
    case "SET_SORTING":
      return { ...state, sorting: action.payload };
    case "SET_COLUMN_ORDER":
      return { ...state, columnOrder: action.payload };
    case "SET_COLUMN_VISIBILITY":
      return { ...state, columnVisibility: action.payload };
    case "SET_VIEWING_EXCEPTION":
      return { ...state, viewingException: action.payload };
    case "OPEN_DETAIL":
        return { ...state, detailIsOpen: true };
    case "CLOSE_DETAIL":
      return { ...state, detailIsOpen: false, viewingException: null };
    case "SET_ROW_SELECTION":
      return { ...state, rowSelection: action.payload };
    case "RESET_ROW_SELECTION":
      return { ...state, rowSelection: {} };
    default:
      return state;
  }
}

export function Exceptions({ history, globalSearch, currentGeos }: { history: any; globalSearch: string; currentGeos: string[] }) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [state, dispatch] = useReducer(exceptionsPageReducer, {
    loading: false,
    error: null,
    data: null,
    configurationIsOpen: false,
    sorting: [{id: 'priority', desc: true}],
    columnOrder: [],
    columnVisibility: getAllColumnsVisible(),
    viewingException: null,
    detailIsOpen: false,
    rowSelection: {}
  });
  
  console.log('Globale search value:', globalSearch);
  console.log("Exceptions page selected row id:", state.rowSelection);
  const { id } = useParams<{ id?: string }>();
  console.log("Current ID from URL:", id);

  const navigate = useNavigate();
  // const handleDetailsClick = (exception: Exception) => {
  //   dispatch({ type: "SET_VIEWING_EXCEPTION", payload: exception });
  //   dispatch({ type: "OPEN_DETAIL" });
  //   history.push(`/exceptions/${exception.id}`); 
  // };

  const handleCloseDetail = React.useCallback(() => {
    dispatch({ type: "SET_VIEWING_EXCEPTION", payload: null });
    dispatch({ type: "CLOSE_DETAIL" });
    dispatch({ type: "RESET_ROW_SELECTION" });
    navigate("/exceptions", {replace: true}); 

  }, [dispatch, navigate]);
  

  // const debouncedGetExceptions = useMemo(() => {
  //   debounce((sorting: SortingState, search: string) => {
  //     dispatch({ type: "FETCH_START" });
  //     api.getExceptions(sorting, search)
  //       .then((result) => {
  //         dispatch({ type: "FETCH_SUCCESS", payload: result });
  //       })
  //       .catch((err) => {
  //         dispatch({ type: "FETCH_ERROR", payload: err.message });
  //       });
  //   }, 300);
  // }, []);
  
  React.useEffect(() => {
    let cancelled = false;
    dispatch({ type: "FETCH_START" });
    api.getExceptions(state.sorting, globalSearch, currentGeos)
      .then((result) => {
        if (!cancelled) dispatch({ type: "FETCH_SUCCESS", payload: result });
      })
      .catch((err) => {
        //TODO: look at staus code and handle different errors
        if (!cancelled) dispatch({ type: "FETCH_ERROR", payload: err.message });
      });
    return () => { cancelled = true; };
  }, [state.sorting, globalSearch, currentGeos]);

  const openConfiguration = () => dispatch({ type: "OPEN_CONFIGURATION" });

  const rows = React.useMemo(
    () => state.data?.data?.exception?.list?.items ?? [],
    [state.data]
  );

  const handleRowSelectionChange = (newRowSelection: RowSelectionState) => {
    const exceptionId = Object.keys(newRowSelection)[0];
    const exception: Exception | null = rows.find((ex) => ex.id === exceptionId);
    dispatch({ type: "SET_ROW_SELECTION", payload: newRowSelection });
    dispatch({ type: "SET_VIEWING_EXCEPTION", payload: exception });
    dispatch({ type: "OPEN_DETAIL" });
    if (exceptionId) {
      navigate(`/exceptions/${exceptionId}`, { replace: true }); 
    }
  };

  React.useEffect(() => {
    if (id && rows.length > 0) {
        console.log("Finding exception with ID:", id, rows);
        const exception: Exception | undefined = rows.find((ex) => ex.id === id);
        console.log("Found exception:", exception);
        if (exception) {
            // Set row selection first
            dispatch({ type: "SET_ROW_SELECTION", payload: { [exception.id]: true } });
            dispatch({ type: "SET_VIEWING_EXCEPTION", payload: exception });
            dispatch({ type: "OPEN_DETAIL" });
        }
    }
  }, [id, rows]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && state.detailIsOpen) {
        handleCloseDetail();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [state.detailIsOpen, handleCloseDetail]);

  return (
    <Page documentTitle="Exceptions - Customs Compliance Hub" width="full">
      <PageHeader
        title="Exceptions"
        summary="Manage and resolve compliance and data issues. This list is sortable and filterable, with critical information frozen for easy reference as you scroll."
        actions={<IconButton icon='Settings' onClick={openConfiguration} label={''} />}
      />

      <ColumnsSidePanel
        isOpen={state.configurationIsOpen}
        onClose={() => dispatch({ type: "CLOSE_CONFIGURATION" })}
        columnVisibility={state.columnVisibility}
        setColumnVisibility={(value) => {
          const newVisibility =
            typeof value === "function" ? value(state.columnVisibility) : value;
          dispatch({ type: "SET_COLUMN_VISIBILITY", payload: newVisibility });
        }}
        columns={getTableColumns().map(col => ({
          id: typeof col.accessorKey === "string" ? col.accessorKey : col.id,
          header: typeof col.header === "string" ? col.header : String(col.header)
        }))}
      />

      <ExceptionDetail
        isOpen={state.detailIsOpen}
        onClose={handleCloseDetail}
        viewingException={state.viewingException}
      />
      
      <div className='eds-flex'>
        <ExceptionsTable
            columns={() => getTableColumns()}
            data={rows}
            getRowId={(originalRow: Exception) => originalRow.id}
            state={{ 
                sorting: state.sorting, 
                columnFilters, 
                setSorting: (newSorting: SortingState) => dispatch({ type: "SET_SORTING", payload: newSorting }), 
                setColumnFilters,
                columnOrder: state.columnOrder,
                setColumnOrder: (newOrder: string[]) => dispatch({ type: "SET_COLUMN_ORDER", payload: newOrder }),
                setRowSelection: handleRowSelectionChange,
                rowSelection: state.rowSelection,
                resetRowSelection: () => dispatch({ type: "RESET_ROW_SELECTION" }),
                columnVisibility: state.columnVisibility,
                setColumnVisibility: (newVisibility: VisibilityState) => dispatch({ type: "SET_COLUMN_VISIBILITY", payload: newVisibility })
            }}
            isLoading={state.loading}
            error={state.error}
        />
      </div>
    </Page>
  );
}
